import React, { useEffect, useState } from 'react';
import { Label } from 'reactstrap';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import styled from 'styled-components';
import Papa from 'papaparse';
import { Wizard, Steps, Step } from 'react-albus';

import BackButton from '../../custom/BackButton';
import ActionIcon from '../../custom/ActionIcon';
import { Input } from 'reactstrap';
import Lottie from 'lottie-react';
import useUser from '../../../customHooks/useUser';
import { getTsDate } from '../../../utils';
import TopNavigation from '../../wizard-hooks/TopNavigation';
import BottomNavigation from '../../wizard-hooks/BottomNavigation';
import { IStep, PushStep, GoToNextStep } from '../../../globals/interfaces';
const upload = require('../../../assets/lottie/upload.json');

type UploadType = 'iac' | 'ccsf' | 'icsf' | 'shippers';
type ParseType = UploadType | '001';
type UploadTypeArray = Array<{ name: string; value: string; }>;

interface Props {
    modal: boolean;
    setModal: (state: boolean) => void;
    type: UploadType;
    submit: (data: Array<any>) => Promise<void>;
}

export default function ModalUploadCsv({
    modal,
    setModal,
    type,
    submit
}: Props) {

    const { user } = useUser();
    const [jsonData, setJsonData] = useState(null);
    const [fileName, setFileName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const isError = Boolean(error);

    // IAC upload wizard: step 1 uploads IAC data, step 2 uploads IACSSP 001 data,
    // which is then used to look up and overwrite IACSSP_08_001 on the IAC records.
    const [iacData, setIacData] = useState<Array<any> | null>(null);
    const [iacFileName, setIacFileName] = useState('');
    const [ccsf001Data, setCcsf001Data] = useState<Array<any> | null>(null);
    const [ccsf001FileName, setCcsf001FileName] = useState('');

    const requiredColumns: Record<ParseType, UploadTypeArray> = {
        iac: [{
            name: 'Approval Number',
            value: 'approval_number'
        }, {
            name: 'Indirect Carrier Name',
            value: 'indirect_carrier_name'
        }, {
            name: 'City',
            value: 'city'
        }, {
            name: 'State',
            value: 'state'
        }, {
            name: 'Postal code',
            value: 'postal_code'
        }, {
            name: 'Expiration',
            value: 'expiration_date'
        }, {
            name: 'Type',
            value: 'IACSSP_08_001'
        }],
        '001': [{
            name: 'Approval Number',
            value: 'approval_number'
        }, {
            name: 'Indirect Carrier Name',
            value: 'indirect_carrier_name'
        }, {
            name: 'City',
            value: 'city'
        }, {
            name: 'State',
            value: 'state'
        }, {
            name: 'Type',
            value: 'type'
        }],
        ccsf: [{
            name: 'Approval Number',
            value: 'approval_number'
        }, {
            name: 'Certified Cargo Screening Facility Name',
            value: 'certified_cargo_screening_facility_name'
        }, {
            name: 'Street Address',
            value: 'street_address'
        }, {
            name: 'City',
            value: 'city'
        }, {
            name: 'State',
            value: 'state'
        }, {
            name: 'CCSF Expiration Date',
            value: 'ccsf_expiration_date'
        }, {
            name: 'IAC Number',
            value: 'iac_number'
        }],
        icsf: [{
            name: 'Approval Number',
            value: 'approval_number'
        }, {
            name: 'Certified Cargo Screening Facility Name',
            value: 'certified_cargo_screening_facility_name'
        }, {
            name: 'Street Address',
            value: 'street_address'
        }, {
            name: 'City',
            value: 'city'
        }, {
            name: 'State',
            value: 'state'
        }, {
            name: 'CCSF Expiration Date',
            value: 'ccsf_expiration_date'
        }],
        shippers: [{
            name: 'Approval Number',
            value: 'approval_number'
        }, {
            name: 'Certified Cargo Screening Facility Name',
            value: 'certified_cargo_screening_facility_name'
        }, {
            name: 'Street Address',
            value: 'street_address'
        }, {
            name: 'City',
            value: 'city'
        }, {
            name: 'State',
            value: 'state'
        }, {
            name: 'CCSF Expiration Date',
            value: 'ccsf_expiration_date'
        }]
    };

    const validateHeaders = (headers, type: ParseType) => {
        const required: UploadTypeArray = requiredColumns[type];
        const requiredNames = required.map(col => col.name);
        const missing = required.filter(col => !headers.includes(col.name));
        const extra = headers.filter(col => !requiredNames.includes(col));

        if (missing.length > 0) {
            return {
                isValid: false,
                message: `Missing required columns for ${type}: ${missing.map(col => col.name).join(', ')}`
            };
        }

        if (extra.length > 0) {
            return {
                isValid: false,
                message: `Unexpected columns found for ${type}: ${extra.join(', ')}. Only these columns are allowed: ${requiredNames.join(', ')}`
            };
        }

        return { isValid: true };
    };

    const transformDataToDbFormat = (csvData: any[], type: ParseType) => {
        const columnMapping = requiredColumns[type];

        return csvData.map(record => {
            const transformedRecord = {};

            // Map CSV column names to database field names
            columnMapping.forEach(({ name, value }) => {
                if (record.hasOwnProperty(name)) {
                    transformedRecord[value] = record[name];
                }
            });

            return transformedRecord;
        });
    };

    // Builds a map of approval_number -> IACSSP 001 type, equivalent to Table2 in
    // =IFERROR(VLOOKUP([@[approval_number]],Table2,5,0),"Not CCSF")
    const buildCcsf001Map = (data: Array<any>) => {
        const map = new Map<string, string>();
        data.forEach(record => map.set(record.approval_number, record.type));
        return map;
    };

    const mergeIacWithCcsf001 = (iac: Array<any>, ccsf001: Array<any>) => {
        const map = buildCcsf001Map(ccsf001);
        return iac.map(record => ({
            ...record,
            IACSSP_08_001: map.has(record.approval_number) ? map.get(record.approval_number) : 'Not CCSF'
        }));
    };

    const handleFileUpload = (
        event: React.ChangeEvent<HTMLInputElement>,
        parseType: ParseType,
        setFileName: (name: string) => void,
        setData: (data: Array<any>) => void
    ) => {
        const file = event.target.files[0];

        if (!file) return;

        // Check if file is CSV
        if (!file.name.toLowerCase().endsWith('.csv')) {
            setError('Please select a CSV file');
            return;
        }

        setIsLoading(true);
        setError('');
        setFileName(file.name);

        // First, parse just the headers to validate them
        Papa.parse(file, {
            header: true,
            preview: 1, // Only read first row to get headers
            skipEmptyLines: true,
            complete: (headerResults) => {
                if (headerResults.errors.length > 0) {
                    setIsLoading(false);
                    setError(`Header parse errors: ${headerResults.errors.map(e => e.message).join(', ')}`);
                    return;
                }

                // Get the headers from the first row
                const headers = headerResults.meta.fields || [];

                // Validate headers based on file type
                const validation = validateHeaders(headers, parseType);

                if (!validation.isValid) {
                    setIsLoading(false);
                    setError(validation.message);
                    return;
                }

                // If validation passes, parse the full file
                Papa.parse(file, {
                    header: true,
                    skipEmptyLines: true,
                    dynamicTyping: true,
                    complete: (results) => {
                        setIsLoading(false);

                        if (results.errors.length > 0) {
                            setError(`Parse errors: ${results.errors.map(e => e.message).join(', ')}`);
                            return;
                        }

                        // Transform CSV data to database format
                        const transformedData = transformDataToDbFormat(results.data, parseType);

                        // Add hardcoded fields to each record
                        const enrichedData = transformedData.map((record) => ({
                            ...record,
                            s_created_by: user.s_email,
                            t_created: getTsDate(),
                            s_modified_by: user.s_email,
                            t_modified: getTsDate()
                        }));

                        // Convert to JSON and store
                        setData(enrichedData);
                    },
                    error: (error) => {
                        setIsLoading(false);
                        setError(`Error parsing file: ${error.message}`);
                    }
                });
            },
            error: (error) => {
                setIsLoading(false);
                setError(`Error reading file headers: ${error.message}`);
            }
        });
    };

    const clearData = () => {
        setJsonData(null);
        setFileName('');
        setIacData(null);
        setIacFileName('');
        setCcsf001Data(null);
        setCcsf001FileName('');
        setError('');
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        if (type === 'iac') {
            await submit(mergeIacWithCcsf001(iacData, ccsf001Data));
        } else {
            await submit(jsonData);
        }
        setIsLoading(false);
    }

    const onClickNext = (goToNext: GoToNextStep, _steps: Array<IStep>, step: IStep, _push?: PushStep) => {
        if (step.id === '001') {
            // Last step: submitting happens via the Save button instead of Next.
            return;
        }
        goToNext();
    };

    const onClickPrev = (goToPrev: GoToNextStep) => {
        goToPrev();
    };

    const resolveEnableNext = (step: IStep) => {
        if (step.id === 'IAC') return Boolean(iacData);
        if (step.id === '001') return Boolean(ccsf001Data);
        return false;
    };

    useEffect(() => {
        clearData();
    }, [modal]);

    const toggle = () => setModal(!modal);

    // Note: the Wizard itself must stay mounted while isLoading toggles, otherwise
    // react-albus resets its internal history and jumps back to the first step.
    if (type === 'iac') {
        return (
            <Modal isOpen={modal} toggle={toggle} className="wizard wizard-default">
                <Wizard>
                    <ModalHeader className={'d-flex'}>
                        <HeaderContainer>
                            <BackButton onClick={toggle} />
                            <h4 className={'pl-2'}>Upload IAC</h4>
                        </HeaderContainer>
                    </ModalHeader>
                    <ModalBody>
                        {isLoading ? (
                            <Lottie animationData={upload} />
                        ) : (
                            <>
                                <TopNavigation className="justify-content-center mb-4" disableNav={true} topNavClick={() => { }} />
                                <Steps>
                                    <Step id={'IAC'} name={'IAC'}>
                                        <Label htmlFor="csv-file-input-iac" className="block text-sm font-medium text-gray-700 mb-2">
                                            Select IAC CSV File
                                        </Label>
                                        <Input
                                            id="csv-file-input-iac"
                                            type="file"
                                            accept=".csv"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e, 'iac', setIacFileName, setIacData)}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md"
                                        />
                                        {isError && <h6 className={'text-danger mt-2'}>{error}</h6>}
                                        {iacData && <h6 className={'text-success mt-2'}>Ready: {iacFileName} ({iacData.length} records)</h6>}
                                    </Step>
                                    <Step id={'001'} name={'001'}>
                                        <Label htmlFor="csv-file-input-001" className="block text-sm font-medium text-gray-700 mb-2">
                                            Select IACSSP 001 CSV File
                                        </Label>
                                        <Input
                                            id="csv-file-input-001"
                                            type="file"
                                            accept=".csv"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e, '001', setCcsf001FileName, setCcsf001Data)}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md"
                                        />
                                        <h6 className={isError ? 'text-danger' : 'text-success'}>
                                            {ccsf001Data ? `Ready: ${ccsf001FileName} (${ccsf001Data.length} records)` : error}
                                        </h6>
                                    </Step>
                                </Steps>
                            </>
                        )}
                    </ModalBody>
                    <ExpandedFooter>
                        <BottomNavigation
                            onClickNext={onClickNext}
                            onClickPrev={onClickPrev}
                            className="justify-content-center"
                            prevLabel={'Back'}
                            nextLabel={isLoading ? '...' : 'Next'}
                            resolveEnableNext={(step: IStep) => !isLoading && resolveEnableNext(step)}
                        />
                        {
                            !error && ccsf001Data && (
                                <SaveButtonContainer>
                                    <ActionIcon
                                        type={'save'}
                                        onClick={() => handleSubmit()}
                                        disabled={isError || !ccsf001Data}
                                        confirm={true}
                                        confirmMessage='This will replace existing data. Proceed?'
                                    />
                                </SaveButtonContainer>
                            )
                        }
                    </ExpandedFooter>
                </Wizard>
            </Modal>
        );
    }

    if (isLoading) {
        return (
            <Modal isOpen={modal} toggle={toggle}>
                <ModalBody>
                    <Lottie
                        animationData={upload}
                    />
                </ModalBody>
            </Modal>
        );
    }

    return (
        <Modal isOpen={modal} toggle={toggle}>
            <ModalHeader className={'d-flex'}>
                <HeaderContainer>
                    <BackButton onClick={toggle} />
                    <h4 className={'pl-2'}>Upload {type.toUpperCase()}</h4>
                </HeaderContainer>
            </ModalHeader>
            <ModalBody>
                <Label htmlFor="csv-file-input" className="block text-sm font-medium text-gray-700 mb-2">
                    Select CSV File
                </Label>
                <Input
                    id="csv-file-input"
                    type="file"
                    accept=".csv"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e, type, setFileName, setJsonData)}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-md"
                />
            </ModalBody>
            <ExpandedFooter>
                <FooterContentContainer>
                    <FooterButtonsContainer update={isError || Boolean(jsonData)}>
                        <h6 className={isError ? 'text-danger' : 'text-success'}>{jsonData ? `Ready to upload file (${jsonData.length} records)` : error}</h6>
                        <ActionIcon
                            type={'save'}
                            onClick={() => handleSubmit()}
                            disabled={isError || !jsonData}
                            confirm={true}
                            confirmMessage='This will replace existing data. Proceed?'
                        />
                    </FooterButtonsContainer>
                </FooterContentContainer>
            </ExpandedFooter>
        </Modal>
    );
}

const HeaderContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
`;

const ExpandedFooter = styled(ModalFooter)`
    width: 100%;
`;

const FooterContentContainer = styled.div`
    width: 100%;
`;

const FooterButtonsContainer = styled.div<{ update?: boolean }>`
    width: 100%;
    display: flex;
    justify-content: ${p => p.update ? 'space-between' : 'flex-end'};
`;

const SaveButtonContainer = styled.div`
    margin-top: 15px;
`;
